import { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InventoryStockTable } from '@/components/inventory/InventoryStockTable';
import { InventoryKPICards } from '@/components/inventory/InventoryKPICards';
import { TransactionHistory } from '@/components/inventory/TransactionHistory';
import { StockAdjustmentDialog } from '@/components/inventory/StockAdjustmentDialog';
import { DamageRecordDialog } from '@/components/inventory/DamageRecordDialog';
import { useInventoryStock, useLowStockProducts } from '@/hooks/useInventory';
import { Package, ArrowLeftRight, AlertTriangle, Plus, MinusCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export default function Inventory() {
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [damageDialogOpen, setDamageDialogOpen] = useState(false);

  const { data: stock, isLoading: stockLoading } = useInventoryStock();
  const { data: lowStockProducts } = useLowStockProducts();
  const lowStockCount = lowStockProducts?.length || 0;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Inventory</h1>
            <p className="text-muted-foreground">
              Track stock levels and manage inventory movements
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setDamageDialogOpen(true)}>
              <MinusCircle className="mr-2 h-4 w-4" />
              Record Damage
            </Button>
            <Button onClick={() => setAdjustmentDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Adjust Stock
            </Button>
          </div>
        </div>

        {/* KPI Summary Cards */}
        <InventoryKPICards stock={stock} isLoading={stockLoading} />

        {/* Low Stock Alert - improved with mini table */}
        {lowStockCount > 0 && (
          <Card className="border-destructive bg-destructive/5">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <CardTitle className="text-lg">Low Stock Alert</CardTitle>
                <Badge variant="destructive">{lowStockCount}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Product</TableHead>
                    <TableHead className="text-xs text-right">Current</TableHead>
                    <TableHead className="text-xs text-right">Reorder Level</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lowStockProducts?.slice(0, 5).map((product) => (
                    <TableRow key={product.product_id}>
                      <TableCell className="py-1.5 font-medium text-sm">{product.product_name}</TableCell>
                      <TableCell className="py-1.5 text-right text-sm font-mono text-destructive">
                        {product.current_quantity.toLocaleString()} {product.unit_of_measure}
                      </TableCell>
                      <TableCell className="py-1.5 text-right text-sm font-mono text-muted-foreground">
                        {product.reorder_level?.toLocaleString() || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {lowStockCount > 5 && (
                <p className="text-xs text-muted-foreground mt-2">
                  +{lowStockCount - 5} more products below reorder level
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="stock" className="space-y-4">
          <TabsList>
            <TabsTrigger value="stock" className="gap-2">
              <Package className="h-4 w-4" />
              Stock Levels
            </TabsTrigger>
            <TabsTrigger value="transactions" className="gap-2">
              <ArrowLeftRight className="h-4 w-4" />
              Transactions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stock">
            <Card>
              <CardHeader>
                <CardTitle>Current Stock</CardTitle>
                <CardDescription>
                  View and manage current stock levels for all products
                </CardDescription>
              </CardHeader>
              <CardContent>
                <InventoryStockTable />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Transaction History</CardTitle>
                <CardDescription>
                  View all inventory movements including receipts, sales, adjustments, and damages
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TransactionHistory />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <StockAdjustmentDialog 
        open={adjustmentDialogOpen} 
        onOpenChange={setAdjustmentDialogOpen} 
      />
      <DamageRecordDialog 
        open={damageDialogOpen} 
        onOpenChange={setDamageDialogOpen} 
      />
    </Layout>
  );
}
